import LegSection from '@/components/purchase/checkout/ancillaries/LegSection';
import AncillaryModal from '@/components/purchase/checkout/ancillaries/ancillary-modal/AncillaryModal';
import {
  getPurchaseFlowBookingAdditionalOffersSearch,
  getPurchaseFlowBookingById,
  removePurchaseFlowBookingAncillary,
} from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingSelector } from '@/features/purchase/purchaseSelectors';
import { useBookingTripLegs } from '@/hooks/useBookingTripLegs';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { BookedAncillaryDTO, LegDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Divider, Typography } from '@fleet/webshop-ui';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { toast } from 'react-toastify';

interface AncillaryListProps {}

const AncillaryList: FC<AncillaryListProps> = () => {
  const dispatch = useDispatch();
  const [selectedLeg, setSelectedLeg] = useState<LegDTO>();
  const booking = useSelector(purchaseFlowBookingSelector);
  const { outboundLegs, inboundLegs } = useBookingTripLegs(booking);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  const handlePurchaseFlowBookingAdditionalOffersSearch =
    useCallback(async () => {
      try {
        if (booking?.id) {
          await dispatch(
            getPurchaseFlowBookingAdditionalOffersSearch(booking.id)
          ).unwrap();
        }
      } catch {
        toast.error(<TransAlert i18nKey="failedToRetrieveAvailableExtras" />);
      }
    }, [booking?.id, dispatch]);

  useEffect(() => {
    handlePurchaseFlowBookingAdditionalOffersSearch();
  }, [handlePurchaseFlowBookingAdditionalOffersSearch]);

  const handleRemoveAncillary = useCallback(
    async (ancillary: BookedAncillaryDTO): Promise<void> => {
      if (!ancillary.id || !ancillary.bookedOfferId) {
        return;
      }

      await dispatch(
        removePurchaseFlowBookingAncillary({
          ancillaryId: ancillary.id,
          bookedOfferId: ancillary.bookedOfferId,
        })
      ).unwrap();
      await dispatch(getPurchaseFlowBookingById(booking?.id)).unwrap();
    },
    [booking, dispatch]
  );

  const renderLegWithChosenAncillaries = useCallback(
    (leg: LegDTO, index: number, array: Array<LegDTO>) => (
      <LegSection
        key={index}
        leg={leg}
        classNames={{
          accordionItem: cn({ 'border-none pb-0': index === array.length - 1 }),
        }}
        handleRemoveAncillary={handleRemoveAncillary}
        changeSelectedLeg={() => setSelectedLeg(leg)}
      />
    ),
    [handleRemoveAncillary]
  );

  const changeIsOverlayOpened = useCallback(
    (value: boolean) => {
      if (!value) {
        setSelectedLeg(undefined);
      }
    },
    [setSelectedLeg]
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col laptop:flex-row laptop:gap-6">
        <div className="flex flex-1 flex-col">
          <Typography variant="body1-bold" as="h3" className="mb-3">
            <TransText i18nKey="outboundTrip" />
          </Typography>
          <div className="flex flex-col gap-5">
            {outboundLegs.map(renderLegWithChosenAncillaries)}
          </div>
        </div>
        {inboundLegs.length ? (
          <>
            <Divider
              className={cn('my-3 h-auto border-neutral-pale', {
                'mb-5 mt-1 max-w-[1px]': isLaptopOrBigger,
              })}
              orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
              variant="medium"
            />
            <div className="flex flex-1 flex-col">
              <Typography variant="body1-bold" as="h3" className="mb-3">
                <TransText i18nKey="returnTrip" />
              </Typography>
              <div className="flex flex-col gap-5">
                {inboundLegs.map(renderLegWithChosenAncillaries)}
              </div>
            </div>
          </>
        ) : null}
      </div>
      <AncillaryModal
        selectedLeg={selectedLeg}
        changeIsOverlayOpened={changeIsOverlayOpened}
      />
    </div>
  );
};

export default AncillaryList;
